/**
 * How a group's drops use its lists, as ONE choice (user, 19/10/2026: "I
 * don't understand the difference between every list and alternate"):
 * One list · Every list (each drop is copied to every list) · Alternate
 * (each drop goes to the next list). Stored as before on the link:
 * listsPerDay 1 = one; N ≥ 2 = all lists up to N, with allListsMode
 * EACH (every) or ALTERNATE.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenAllListsMode } from '@trafficpulse/shared';

import type { DraftGroupRef } from '../../../../drop-generator.types';
import { ALL_LISTS_CAP } from '../lists-per-day/list-choice';

export type ListMode = 'one' | 'every' | 'alternate';

export function listModeOf(link: Pick<DraftGroupRef, 'listsPerDay' | 'allListsMode'>): ListMode {
  if ((link.listsPerDay ?? 1) === 1) return 'one';
  return link.allListsMode === 'ALTERNATE' ? 'alternate' : 'every';
}

/** The link patch for a mode; a group already on all lists keeps its "up to". */
export function modePatch(mode: ListMode, listsPerDay: number): { listsPerDay: number; allListsMode?: DropGenAllListsMode } {
  if (mode === 'one') return { listsPerDay: 1 };
  const upTo = listsPerDay > 1 ? listsPerDay : ALL_LISTS_CAP.default;
  return { listsPerDay: upTo, allListsMode: mode === 'every' ? 'EACH' : 'ALTERNATE' };
}

export const MODE_CARDS: ReadonlyArray<{ mode: ListMode; title: string; caption: string; hint: string }> = [
  {
    mode: 'one',
    title: 'One list',
    caption: 'Every drop → the best list',
    hint: 'The whole day goes to one list — the best by the formula. When the list verdict burns it, the next list takes over the next day.',
  },
  {
    mode: 'every',
    title: 'Every list',
    caption: 'Each drop → all the lists',
    hint: 'Each drop is copied to every list of the day (up to your limit): 12 drops × 10 lists = 120 sends. Each list is judged on its own.',
  },
  {
    mode: 'alternate',
    title: 'Alternate',
    caption: 'Each drop → the next list',
    hint: 'The lists take turns: drop 1 → the best list, drop 2 → the second, … then round again. The day has as many sends as drops.',
  },
];
